/**
 * Pure layout and graph helpers for the career map (#/careers/map).
 *
 * The map draws every career in the catalog as a node, grouped into one column per family, and
 * draws edges from the focused career to the tracks it is related to. Nothing here touches the DOM
 * or the network: the page passes in catalog data and renders the coordinates that come back, so
 * the geometry can be unit tested.
 *
 * Nothing in here ranks careers. Columns follow the order the API lists families in, nodes are
 * alphabetical inside a column, and "example routes" are literally walks over the related-career
 * links: related skills and training, never a guaranteed progression.
 */
import type { IconName } from '../components/ui/Icon';

export type MapStatus = 'available' | 'planned';

export interface MapCareerInput {
  id: string;
  name: string;
  family: string;
  status: MapStatus;
}

export interface MapNode extends MapCareerInput {
  familyLabel: string;
  /** Zero-based column (one per family) and row inside that column. */
  column: number;
  row: number;
  /** Center of the node in layout coordinates. */
  x: number;
  y: number;
}

export interface MapColumn {
  family: string;
  label: string;
  index: number;
  /** Center of the column in layout coordinates. */
  x: number;
  nodes: MapNode[];
}

export interface MapLayout {
  columns: MapColumn[];
  nodes: MapNode[];
  width: number;
  height: number;
}

export interface MapLayoutOptions {
  columnWidth?: number;
  rowHeight?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
}

/** Geometry defaults, in CSS pixels of the map's own coordinate space. */
export const MAP_DEFAULTS = {
  columnWidth: 204,
  rowHeight: 88,
  paddingX: 20,
  paddingTop: 94,
  paddingBottom: 30,
} as const;

const byName = (a: MapCareerInput, b: MapCareerInput) => a.name.localeCompare(b.name);

/**
 * Group careers into one column per family and give every node a position.
 * Families keep the order of the `families` record; a family with no careers is left out, and a
 * career whose family is missing from the record still gets a column at the end.
 */
export function buildMapLayout(careers: readonly MapCareerInput[], families: Readonly<Record<string, string>>, options: MapLayoutOptions = {}): MapLayout {
  const { columnWidth, rowHeight, paddingX, paddingTop, paddingBottom } = { ...MAP_DEFAULTS, ...options };
  const order: string[] = [];
  for (const family of Object.keys(families)) if (careers.some((c) => c.family === family)) order.push(family);
  for (const c of careers) if (!order.includes(c.family)) order.push(c.family);

  const columns: MapColumn[] = [];
  const nodes: MapNode[] = [];
  let maxRows = 0;
  order.forEach((family, index) => {
    const x = paddingX + index * columnWidth + columnWidth / 2;
    const members = careers.filter((c) => c.family === family).slice().sort(byName);
    const columnNodes = members.map((c, row) => ({
      ...c,
      familyLabel: families[family] ?? family,
      column: index,
      row,
      x,
      y: paddingTop + row * rowHeight + rowHeight / 2,
    }));
    maxRows = Math.max(maxRows, columnNodes.length);
    nodes.push(...columnNodes);
    columns.push({ family, label: families[family] ?? family, index, x, nodes: columnNodes });
  });

  return {
    columns,
    nodes,
    width: paddingX * 2 + Math.max(1, order.length) * columnWidth,
    height: paddingTop + maxRows * rowHeight + paddingBottom,
  };
}

export function findNode(layout: MapLayout, id: string | undefined): MapNode | undefined {
  return id ? layout.nodes.find((n) => n.id === id) : undefined;
}

export interface MapLink {
  id: string;
  /** Share of skills the two tracks have in common, 0..1, when the API knows it. */
  overlap?: number;
}

export interface MapEdge {
  from: string;
  to: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  overlap?: number;
}

/**
 * Edges from the focused career to each track it links to. Links that are not on the map, point at
 * the focused career itself, or repeat an earlier link are dropped, so the drawing never doubles up.
 */
export function focusEdges(layout: MapLayout, focusId: string | undefined, links: readonly MapLink[]): MapEdge[] {
  const from = findNode(layout, focusId);
  if (!from) return [];
  const seen = new Set<string>([from.id]);
  const edges: MapEdge[] = [];
  for (const link of links) {
    if (seen.has(link.id)) continue;
    const to = findNode(layout, link.id);
    if (!to) continue;
    seen.add(link.id);
    edges.push({ from: from.id, to: to.id, x1: from.x, y1: from.y, x2: to.x, y2: to.y, ...(link.overlap === undefined ? {} : { overlap: link.overlap }) });
  }
  return edges;
}

const round = (n: number) => Math.round(n * 100) / 100;

/** A horizontal cubic curve between the two ends of an edge, for an SVG `d` attribute. */
export function edgePath(edge: MapEdge): string {
  const dx = edge.x2 - edge.x1;
  const reach = Math.max(48, Math.abs(dx) * 0.45);
  const dir = dx < 0 ? -1 : 1;
  const c1 = round(edge.x1 + reach * dir);
  const c2 = round(edge.x2 - reach * dir);
  return `M ${round(edge.x1)} ${round(edge.y1)} C ${c1} ${round(edge.y1)}, ${c2} ${round(edge.y2)}, ${round(edge.x2)} ${round(edge.y2)}`;
}

/** Whole percent, for labels and meters. Overlap is descriptive: it never ranks one track above another. */
export function overlapPercent(overlap: number): number {
  return Math.round(Math.max(0, Math.min(1, overlap)) * 100);
}

export interface ExampleRouteOptions {
  /** Number of careers in a route, including the starting one. */
  maxLength?: number;
  /** How many routes to return. */
  limit?: number;
}

/**
 * Walks the related-career links outward from `start` to build example routes such as
 * "Help Desk Analyst → Systems Analyst → Cloud Support Associate". These are routes other people
 * explore because the skills and training overlap, not a promotion ladder and not a guarantee.
 *
 * `relatedOf` only needs the links the page has actually loaded; a career with no entry simply ends
 * its route. Routes never revisit a career, and a route that is a prefix of a longer one is dropped.
 */
export function exampleRoutes(start: string, relatedOf: ReadonlyMap<string, readonly string[]>, options: ExampleRouteOptions = {}): string[][] {
  const maxLength = Math.max(2, options.maxLength ?? 3);
  const limit = Math.max(0, options.limit ?? 3);
  if (limit === 0) return [];
  let routes: string[][] = (relatedOf.get(start) ?? []).filter((id) => id !== start).map((id) => [start, id]);
  for (let depth = 2; depth < maxLength; depth += 1) {
    const grown: string[][] = [];
    for (const route of routes) {
      const last = route[route.length - 1] as string;
      const next = (relatedOf.get(last) ?? []).filter((id) => !route.includes(id));
      if (next.length === 0) {
        grown.push(route);
        continue;
      }
      // One continuation per route keeps the set readable and deterministic.
      grown.push([...route, next[0] as string]);
    }
    routes = grown;
  }
  const kept: string[][] = [];
  for (const route of routes) {
    if (kept.some((other) => other.length >= route.length && route.every((id, i) => other[i] === id))) continue;
    kept.push(route);
    if (kept.length === limit) break;
  }
  return kept;
}

/** "Help Desk Analyst → Systems Analyst → Cloud Support Associate" for a route of career ids. */
export function routeLabel(route: readonly string[], nameOf: (id: string) => string): string {
  return route.map(nameOf).join(' → ');
}

/* ---------------------------------------------------------------- family presentation ---------------------------------------------------------------- */

/**
 * One icon per career family, so a family reads the same on the landing page, the discovery page
 * and the map. Families are descriptive groupings: the icon carries no ranking.
 */
export const FAMILY_ICONS: Record<string, IconName> = {
  'it-technical-operations': 'terminal',
  'software-engineering': 'code',
  'systems-business': 'briefcase',
  data: 'chart',
  'cloud-security': 'shield',
  'customer-service-delivery': 'users',
  'it-management-governance': 'clipboard',
};

export function familyIcon(family: string): IconName {
  return FAMILY_ICONS[family] ?? 'compass';
}
